/**
 * Artwork Type Definitions
 * Based on SPECIFICATION.md
 */

export interface PlyBBox {
  min: { x: number; y: number; z: number }
  max: { x: number; y: number; z: number }
  center: { x: number; y: number; z: number }
  size: { x: number; y: number; z: number }
}

export interface Artwork {
  id: string
  slug: string
  title: string
  /** Auto-translated English title (or manual override) from .en.txt sidecar. Null when not yet translated. */
  title_en?: string | null
  artist: string
  year?: string | null
  material?: string | null
  dimensions?: string | null
  description: string
  /** Auto-translated English markdown body. Null when no .en.txt exists yet. */
  description_en?: string | null
  categoryId: string
  files: {
    glb?: string | null
    ply?: string | null
    image?: string | null
    thumbnail?: string | null
  }
  plyBBox?: PlyBBox
  featured: boolean
  order: number
}

/**
 * Helper to determine artwork media type
 */
export type ArtworkMediaType = 'glb' | 'ply' | 'image' | 'none'

export function getArtworkMediaType(artwork: Artwork): ArtworkMediaType {
  if (artwork.files.glb) return 'glb'
  if (artwork.files.ply) return 'ply'
  if (artwork.files.image) return 'image'
  return 'none'
}

export function hasMedia(artwork: Artwork): boolean {
  return !!(artwork.files.glb || artwork.files.ply || artwork.files.image)
}

export interface Category {
  id: string
  slug: string
  title: string
  description: string
  description_en?: string | null
  thumbnailUrl?: string | null
  artworkCount: number
  order: number
}

export interface SectionImage {
  url: string
  alt: string
  order: number
}

export interface SectionPage {
  slug: string
  title: string
  title_en?: string | null
  description: string
  description_en?: string | null
  order: number
}

export interface SectionProject {
  slug: string
  title: string
  title_en?: string | null
  description: string
  description_en?: string | null
  order: number
  images: SectionImage[]
}

export type SectionType = 'gallery' | 'content' | 'projects' | 'page' | 'unknown'

export interface MenuSection {
  id: string
  slug: string
  title: string
  order: number
  folder: string
  type: SectionType
  /** content sections only */
  pages?: SectionPage[]
  /** projects sections only */
  intro?: string
  intro_en?: string | null
  projects?: SectionProject[]
  /** single-page sections only */
  pageTitle?: string
  pageTitle_en?: string | null
  description?: string
  description_en?: string | null
}

export interface ContentIndex {
  version: string
  lastUpdated: string
  menuSections?: MenuSection[]
  categories: Category[]
  artworks: Artwork[]
  featured: string[]
}
